/**
 * PlanCard Component
 *
 * This card contains plans for the v2 billing system and is referenced at payment plan.
 * Displays subscription plan details with editing capabilities for the current Professional plan.
 */

import React from 'react';
import {
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Typography,
  Box,
  styled,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PendingChangesBanner from './PendingChangesBanner';

// Styled Components
const StyledCard = styled(Card)(({ theme, isCurrent }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: theme.palette.background.paper,
  border: isCurrent ? `2px solid ${theme.palette.text.primary}` : `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  overflow: 'visible',
  boxShadow: isCurrent
    ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  '&:hover': {
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
}));

const CurrentPlanChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: -12,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: '24px',
  borderRadius: '12px',
  zIndex: 1,
  '& .MuiChip-label': {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

const PlanHeader = styled('header')({
  marginBottom: 32,
});

const PlanTitle = styled('h2')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  fontWeight: 600,
  marginBottom: 12,
  marginTop: 0,
}));

const PlanDescription = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  marginBottom: 24,
  marginTop: 0,
}));

const PriceContainer = styled(Grid)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
});

const Price = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '2.5rem',
  fontWeight: 700,
  lineHeight: 1,
}));

const Period = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: '0.875rem',
}));

const FeaturesSection = styled('section')({
  marginBottom: 32,
});

const FeaturesSectionTitle = styled('h3')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  marginBottom: 16,
  marginTop: 0,
}));

const StyledList = styled(List)({
  padding: 0,
});

const AnimatedFeature = styled(Box)(({ animation }) => ({
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transform:
    animation === 'up'
      ? 'translateY(-8px)'
      : animation === 'down'
      ? 'translateY(8px)'
      : 'translateY(0px)',
  opacity: animation ? 0.7 : 1,
  display: 'inline-block',
}));

const ActionButton = styled(Button)(({ theme, isDowngrade, buttonText }) => ({
  marginTop: 'auto',
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 16,
  paddingRight: 16,
  fontWeight: 500,
  fontSize: '0.8rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: isDowngrade
    ? 'transparent'
    : buttonText === 'Talk to Sales'
    ? 'transparent'
    : theme.palette.text.primary,
  color: isDowngrade
    ? theme.palette.error.main
    : buttonText === 'Talk to Sales'
    ? theme.palette.text.primary
    : theme.palette.background.paper,
  border: isDowngrade
    ? `1px solid ${theme.palette.error.light}`
    : buttonText === 'Talk to Sales'
    ? `1px solid ${theme.palette.divider}`
    : 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: isDowngrade
      ? 'var(--status-red-bg)'
      : buttonText === 'Talk to Sales'
      ? theme.palette.action.hover
      : theme.palette.mode === 'dark' ? theme.palette.action.selected : '#262626',
    boxShadow: 'none',
    borderColor: isDowngrade
      ? theme.palette.error.main
      : buttonText === 'Talk to Sales'
      ? theme.palette.custom.border
      : 'none',
  },
}));

const EditButton = styled(Button)(({ theme, hasPendingChanges }) => ({
  marginTop: 'auto',
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 16,
  paddingRight: 16,
  fontWeight: 500,
  fontSize: '0.8rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: hasPendingChanges ? theme.palette.error.main : theme.palette.text.primary,
  border: hasPendingChanges ? `1px solid ${theme.palette.error.light}` : `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: hasPendingChanges ? 'var(--status-red-bg)' : theme.palette.action.hover,
    boxShadow: 'none',
    borderColor: hasPendingChanges ? theme.palette.error.main : theme.palette.custom.border,
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 16,
  paddingRight: 16,
  fontWeight: 500,
  fontSize: '0.8rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.selected : '#262626',
    boxShadow: 'none',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  paddingTop: 6,
  paddingBottom: 6,
  paddingLeft: 16,
  paddingRight: 16,
  fontWeight: 500,
  fontSize: '0.8rem',
  borderRadius: '6px',
  textTransform: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
    borderColor: theme.palette.custom.border,
  },
}));

const ButtonGrid = styled(Grid)({
  marginTop: 'auto',
  display: 'flex',
  gap: 8,
});

const HelpText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: '0.7rem',
  lineHeight: 1.3,
  textAlign: 'center',
  marginTop: 8,
}));

const PlanCard = ({
  plan,
  isEditing,
  hasPendingChanges,
  pendingChanges,
  currentPlan,
  originalQuantities,
  nextBillingDate,
  onOpenDowngradeDialog,
  onEditMode,
  onSaveChanges,
  onCancelChanges,
  onCancelPendingChanges,
  processing = false,
  onUpgrade,
}) => {
  return (
    <StyledCard isCurrent={plan.isCurrent}>
      {plan.isCurrent && <CurrentPlanChip label="Current Plan" />}

      <CardContent sx={{ flexGrow: 1, p: 4 }}>
        {/* Plan Header */}
        <PlanHeader>
          <PlanTitle>{plan.name}</PlanTitle>
          <PlanDescription>{plan.description}</PlanDescription>
          <PriceContainer>
            <Price>{plan.price}</Price>
            <Period>{plan.period}</Period>
          </PriceContainer>
        </PlanHeader>

        {/* Features List */}
        <FeaturesSection>
          <FeaturesSectionTitle>What's included</FeaturesSectionTitle>
          <StyledList dense>
            {plan.features.map((feature, featureIndex) => {
              const isAnimatedFeature = typeof feature === 'object' && feature.animated;
              const featureText = isAnimatedFeature ? feature.text : feature;
              const animation = isAnimatedFeature ? feature.animationType : null;

              return (
                <ListItem key={featureIndex} sx={{ px: 0, py: 0.5, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                    <CheckIcon
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<AnimatedFeature animation={animation}>{featureText}</AnimatedFeature>}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      lineHeight: 1.4,
                      component: 'div',
                    }}
                  />
                </ListItem>
              );
            })}
          </StyledList>
        </FeaturesSection>

        {/* CTA Button */}
        {!plan.isCurrent && plan.buttonText && (
          <ActionButton
            variant={
              plan.isDowngrade
                ? 'outlined'
                : plan.buttonText === 'Talk to Sales'
                ? 'outlined'
                : 'contained'
            }
            fullWidth
            disabled={plan.isDowngrade && (isEditing || hasPendingChanges || processing)}
            onClick={
              plan.isDowngrade
                ? onOpenDowngradeDialog
                : plan.buttonText === 'Talk to Sales'
                ? () => {
                    window.location.href =
                      'mailto:hello@workflow86.com?subject=Enterprise%20Plan%20Inquiry';
                  }
                : () => onUpgrade?.(plan.name)
            }
            isDowngrade={plan.isDowngrade}
            buttonText={plan.buttonText}
          >
            {plan.buttonText}
          </ActionButton>
        )}

        {/* Pro Plan Edit Controls */}
        {plan.isCurrent && plan.name === 'Professional' && (
          <>
            {/* Pending Changes Banner - Only show in Professional plan */}
            {hasPendingChanges && (
              <PendingChangesBanner
                pendingChanges={pendingChanges}
                currentPlan={currentPlan}
                originalQuantities={originalQuantities}
                nextBillingDate={nextBillingDate}
                onCancelChanges={onCancelPendingChanges}
                disabled={processing}
              />
            )}

            {!isEditing ? (
              <>
                <EditButton
                  variant="outlined"
                  fullWidth
                  onClick={hasPendingChanges ? onCancelPendingChanges : onEditMode}
                  hasPendingChanges={hasPendingChanges}
                  disabled={processing}
                >
                  {processing && hasPendingChanges
                    ? 'Cancelling...'
                    : hasPendingChanges
                    ? 'Cancel Changes'
                    : 'Edit Plan'}
                </EditButton>
                {hasPendingChanges && !processing && (
                  <Grid container justifyContent="center">
                    <HelpText variant="caption">Cancel pending changes to make new edits</HelpText>
                  </Grid>
                )}
              </>
            ) : (
              <ButtonGrid>
                <SaveButton
                  variant="contained"
                  fullWidth
                  onClick={onSaveChanges}
                  disabled={processing}
                >
                  {processing ? 'Saving...' : 'Save Changes'}
                </SaveButton>
                <CancelButton
                  variant="outlined"
                  fullWidth
                  onClick={onCancelChanges}
                  disabled={processing}
                >
                  Cancel
                </CancelButton>
              </ButtonGrid>
            )}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default PlanCard;
